import React, { useCallback, useId, useMemo, useState } from "react"

/**
 * Hover/focus popover (Tailwind) — visible in Storybook; native `title` alone is easy to miss in iframes.
 */
/** API-aligned: topic + languages are always required; region required unless inference is on. */
function ReqBadge() {
  return (
    <span
      className="ml-1.5 inline-flex shrink-0 align-middle rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-800 ring-1 ring-rose-200/80"
      title="Must be set for Get suggestions (see region vs inference)"
    >
      Required
    </span>
  )
}

function OptBadge() {
  return (
    <span
      className="ml-1.5 inline-flex shrink-0 align-middle rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200/80"
      title="May be omitted; server defaults apply where applicable"
    >
      Optional
    </span>
  )
}

function FieldHint({ text }) {
  const uid = useId()
  const tipId = `${uid}-tooltip`
  return (
    <span className="group relative ml-0.5 inline-flex shrink-0 align-middle">
      <button
        type="button"
        className="inline-flex h-5 w-5 cursor-help select-none items-center justify-center rounded-full border border-gray-300 bg-gray-100 dark:bg-gray-800 dark:bg-gray-900 text-[10px] font-bold leading-none text-gray-600 dark:text-gray-400 shadow-sm hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        aria-label={text}
        aria-describedby={tipId}
        onClick={(e) => e.preventDefault()}
      >
        i
      </button>
      <span
        id={tipId}
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-[200] mt-1.5 w-[min(22rem,calc(100vw-2rem))] rounded-md border border-gray-700 bg-gray-900 px-2.5 py-2 text-left text-xs font-normal leading-snug text-white opacity-0 shadow-xl ring-1 ring-black/10 transition-opacity duration-100 group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {text}
      </span>
    </span>
  )
}

const SOURCE_POLICIES = [
  {
    value: "balanced",
    label: "Balanced",
    tip: "Balance official sources, reputable NGOs, and academic or professional society sites appropriate to the topic and region.",
  },
  {
    value: "official_regulators_only",
    label: "Official regulators only",
    tip: "Prefer government ministries, national regulators, and official statistical offices only.",
  },
  {
    value: "official_plus_ngo",
    label: "Official + NGO",
    tip: "Prefer official regulators and reputable national or international NGOs (e.g. WHO, OECD) when relevant to the region.",
  },
  {
    value: "include_academic",
    label: "Include academic",
    tip: "Include universities, national academies, and major non-profit research institutes when they publish authoritative material on the topic.",
  },
  {
    value: "include_publishers",
    label: "Include publishers",
    tip: "You may include established publishers or journals when they host official guidelines or national society positions; still prefer primary institutions when possible.",
  },
]

const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", tip: "ISO 639-1: en. Include in the languages sent to the model for queries and anchoring." },
  { code: "fr", label: "Français", tip: "ISO 639-1: fr. Used for region inference (e.g. FR) when inference is allowed and region is empty." },
  { code: "de", label: "Deutsch", tip: "ISO 639-1: de. Maps to region DE when inferring from language." },
  { code: "es", label: "Español", tip: "ISO 639-1: es. Maps to region ES when inferring from language." },
  { code: "it", label: "Italiano", tip: "ISO 639-1: it. Maps to region IT when inferring from language." },
  { code: "pt", label: "Português", tip: "ISO 639-1: pt. Maps to region PT when inferring from language." },
  { code: "nl", label: "Nederlands", tip: "ISO 639-1: nl. Maps to region NL when inferring from language." },
]

const TIP = {
  panelIntro:
    "Suggest bare hostnames (e.g. has-sante.fr) for site: search. Calls are stateless; the server uses the default LLM unless you pass a model in the API. Wire onSuggest from your backend (HMAC to executor).",
  topic:
    "Free-text intent: what to find institutional sources for. Sent as topic to the model; keep it specific enough to pick relevant domains.",
  region:
    "ISO 3166-1 alpha-2 country code (two letters), e.g. FR or US. Anchors suggestions to that jurisdiction. Leave empty only if you enable region inference below.",
  regionInference:
    "When checked and region is empty, the server may infer a country from your language list (e.g. fr → FR). English-only with no region may fail validation.",
  sourcePolicySelect:
    "Single bias for this request: which kinds of sites the model should prefer. Hover each option in the list for full descriptions.",
  languagesGroup:
    "At least one language required. ISO 639-1 codes are sent to the API and used with region so suggestions are not worldwide-generic.",
  languagesHelp:
    "Short hint: combine with region for localized institutional domains.",
  whitelist:
    "Optional. If non-empty, only hostnames that match these entries (or their registrable domain) are kept after validation.",
  blocklist:
    "Optional per run. Hostnames or registrable domains to reject, merged with any server-side denylist.",
  validateDns:
    "Runs a DNS lookup (getaddrinfo) per candidate after format checks. Stronger signal that the host exists; slower and may fail on flaky DNS.",
  feedback:
    "Free text sent on reshuffle (and available to the model) to steer the next batch, e.g. more regulators, fewer journals.",
  btnInitial:
    "First batch (mode: initial). Up to five domains after validation; may use a repair pass if too many candidates fail checks.",
  btnMore:
    "Mode: more. Requests additional domains that must not repeat any hostname in the session list (previous_domains).",
  btnReshuffle:
    "Mode: reshuffle. Fresh set of up to five; may overlap last batch if the model still considers them best. Pass reshuffle_compare_batch from the last batch.",
  errorBox: "Error message from onSuggest or API (e.g. validation or network).",
  sessionDomains:
    "Hostnames accumulated this session: used as previous_domains for Add 5 more so the server does not repeat them.",
  warnings: "Non-fatal notices from the server (e.g. region inferred, overlap with previous batch).",
  resolvedRegion: "Country code used after resolution or inference; shown for transparency.",
  cacheHit: "True when this response was served from the executor in-process cache (initial mode only, no feedback).",
  suggestionsHeading: "Accepted hostname suggestions with one-line rationale from the model.",
  suggestionRow: "Bare hostname and why it fits your topic, region, and source policy.",
  overlap: "Domains that appeared both in this batch and the previous batch (allowed in reshuffle).",
  rejected: "Candidates that failed validation (PSL, blocklist, DNS, etc.). Expand to inspect reasons.",
  fieldTags:
    "Tags match the executor request: Required = must send a value (or satisfy the region / inference rule). Optional = omitted or empty is valid; defaults apply.",
}

const fieldClass =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"

function emptyResponse() {
  return {
    suggestions: [],
    overlap_with_previous: [],
    rejected: [],
    warnings: [],
    resolved_region: null,
    cache_hit: false,
    error: null,
  }
}

/**
 * Context-aware domain suggestion UI (pairs with POST /tools/domain_suggestions on the executor).
 * Pass `onSuggest` to call your backend (e.g. Rails proxy with HMAC). Without it, actions stay disabled.
 *
 * Default LLM on the server is deepseek-chat unless overridden in the request body.
 */
export default function DomainSuggestionPanel({
  onSuggest,
  readOnly = false,
  /** Shown in header; defaults to deepseek-chat to match executor default */
  defaultModelLabel = "deepseek-chat",
}) {
  const [topic, setTopic] = useState("")
  const [region, setRegion] = useState("")
  const [regionInferenceAllowed, setRegionInferenceAllowed] = useState(false)
  const [languages, setLanguages] = useState(() => new Set(["fr", "en"]))
  const [sourcePolicy, setSourcePolicy] = useState("balanced")
  const [validateDns, setValidateDns] = useState(false)
  const [whitelistText, setWhitelistText] = useState("")
  const [blocklistText, setBlocklistText] = useState("")
  const [userFeedback, setUserFeedback] = useState("")
  const [loading, setLoading] = useState(false)
  const [lastError, setLastError] = useState("")
  const [result, setResult] = useState(() => emptyResponse())
  /** Cumulative hostnames from successful calls (for `more` mode). */
  const [sessionDomains, setSessionDomains] = useState([])
  /** Last successful batch hostnames (for reshuffle overlap). */
  const [lastBatch, setLastBatch] = useState([])

  const canSubmit = useMemo(() => {
    if (!topic.trim() || languages.size === 0) return false
    if (!region.trim() && !regionInferenceAllowed) return false
    return true
  }, [topic, languages.size, region, regionInferenceAllowed])

  const parseLines = (text) =>
    text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)

  const buildPayload = useCallback(
    (mode) => {
      const wl = parseLines(whitelistText)
      const bl = parseLines(blocklistText)
      const langs = [...languages]
      const payload = {
        topic: topic.trim(),
        languages: langs,
        region: region.trim() ? region.trim().toUpperCase().slice(0, 2) : null,
        region_inference_allowed: regionInferenceAllowed,
        source_policy: sourcePolicy,
        mode,
        previous_domains: mode === "more" ? [...sessionDomains] : [],
        reshuffle_compare_batch: mode === "reshuffle" ? [...lastBatch] : [],
        user_feedback: userFeedback.trim() || null,
        domain_whitelist: wl,
        domain_blocklist: bl,
        validate_dns: validateDns,
      }
      return payload
    },
    [
      topic,
      languages,
      region,
      regionInferenceAllowed,
      sourcePolicy,
      whitelistText,
      blocklistText,
      validateDns,
      userFeedback,
      sessionDomains,
      lastBatch,
    ]
  )

  const runSuggest = useCallback(
    async (mode) => {
      if (!onSuggest || !canSubmit) return
      setLoading(true)
      setLastError("")
      try {
        const payload = buildPayload(mode)
        const data = await onSuggest(payload)
        if (data.error) {
          setLastError(typeof data.error === "string" ? data.error : JSON.stringify(data.error))
          setResult(emptyResponse())
          return
        }
        setResult({
          ...emptyResponse(),
          ...data,
          suggestions: data.suggestions || [],
          overlap_with_previous: data.overlap_with_previous || [],
          rejected: data.rejected || [],
          warnings: data.warnings || [],
        })
        const hosts = (data.suggestions || []).map((s) => s.hostname).filter(Boolean)
        if (mode === "initial") {
          setSessionDomains(hosts)
          setLastBatch(hosts)
        } else if (mode === "more") {
          const merged = [...new Set([...sessionDomains, ...hosts])]
          setSessionDomains(merged)
          setLastBatch(hosts)
        } else if (mode === "reshuffle") {
          setLastBatch(hosts)
          setSessionDomains((prev) => [...new Set([...prev, ...hosts])])
        }
      } catch (e) {
        setLastError(e.message || String(e))
        setResult(emptyResponse())
      } finally {
        setLoading(false)
      }
    },
    [onSuggest, canSubmit, buildPayload, sessionDomains]
  )

  const toggleLanguage = (code, checked) => {
    setLanguages((prev) => {
      const next = new Set(prev)
      if (checked) next.add(code)
      else next.delete(code)
      return next
    })
  }

  return (
    <div
      id="domain-suggestion-panel"
      className="domain-suggestion-panel w-full overflow-visible rounded-lg border border-gray-200 bg-white dark:bg-gray-900 p-4 shadow-sm sm:p-6"
    >
      <div className="mb-6 overflow-visible rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-800 sm:text-sm">
        <p className="flex flex-wrap items-center gap-x-1 font-medium text-slate-900">
          <span className="text-slate-600">Domain suggestions</span>
          <FieldHint text={TIP.panelIntro} />
          {" — "}
          institutional hostnames for search (stateless; server default LLM:{" "}
          <code className="rounded bg-white dark:bg-gray-900 px-1 py-0.5 font-mono text-xs ring-1 ring-slate-200">
            {defaultModelLabel}
          </code>
          )
        </p>
        {!onSuggest ? (
          <p className="mt-2 flex flex-wrap items-start gap-1 text-amber-800">
            <span>
              Pass an <code className="font-mono">onSuggest</code> handler to enable requests (e.g. proxy to the executor with
              HMAC).
            </span>
            <FieldHint text="Without onSuggest, buttons stay disabled; implement a Rails proxy that signs POST /tools/domain_suggestions." />
          </p>
        ) : null}
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
          <span className="font-medium text-slate-800">Field tags</span>
          <FieldHint text={TIP.fieldTags} />
          <span className="inline-flex flex-wrap items-center gap-1">
            <ReqBadge />
            <span>must be filled (region is required unless inference is enabled below).</span>
          </span>
          <span className="inline-flex flex-wrap items-center gap-1">
            <OptBadge />
            <span>may be left empty or use defaults.</span>
          </span>
        </p>
      </div>

      <div className={`space-y-6 ${readOnly ? "opacity-70" : ""}`}>
        <div>
          <label className="flex flex-wrap items-center gap-1 text-sm font-medium text-gray-800" htmlFor="ds-topic">
            Topic / intent
            <ReqBadge />
            <FieldHint text={TIP.topic} />
          </label>
          <textarea
            id="ds-topic"
            rows={2}
            className={fieldClass}
            placeholder="What are you trying to find sources for?"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            readOnly={readOnly}
            title={TIP.topic}
            aria-required="true"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="flex flex-wrap items-center gap-1 text-sm font-medium text-gray-800" htmlFor="ds-region">
              Region (ISO 3166-1 alpha-2)
              {regionInferenceAllowed ? <OptBadge /> : <ReqBadge />}
              <FieldHint text={TIP.region} />
            </label>
            <input
              id="ds-region"
              type="text"
              maxLength={2}
              className={`${fieldClass} uppercase`}
              placeholder="e.g. FR"
              value={region}
              onChange={(e) => setRegion(e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 2))}
              readOnly={readOnly}
              title={TIP.region}
              aria-required={!regionInferenceAllowed}
            />
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-indigo-600"
                checked={regionInferenceAllowed}
                onChange={(e) => setRegionInferenceAllowed(e.target.checked)}
                disabled={readOnly}
                title={TIP.regionInference}
              />
              <span className="flex flex-wrap items-center gap-1">
                Allow region inference from languages (when region empty)
                <OptBadge />
                <FieldHint text={TIP.regionInference} />
              </span>
            </label>
          </div>
          <div>
            <span className="flex flex-wrap items-center gap-1 text-sm font-medium text-gray-800">
              Source policy
              <OptBadge />
              <FieldHint text={TIP.sourcePolicySelect} />
            </span>
            <select
              className={fieldClass}
              value={sourcePolicy}
              onChange={(e) => setSourcePolicy(e.target.value)}
              disabled={readOnly}
              title={TIP.sourcePolicySelect}
              aria-describedby="ds-source-policy-hint"
            >
              {SOURCE_POLICIES.map((o) => (
                <option key={o.value} value={o.value} title={o.tip}>
                  {o.label}
                </option>
              ))}
            </select>
            <p id="ds-source-policy-hint" className="mt-1 text-xs text-gray-500">
              {SOURCE_POLICIES.find((p) => p.value === sourcePolicy)?.tip ?? ""}
            </p>
          </div>
        </div>

        <div className="overflow-visible rounded-md border border-gray-100 bg-gray-50 dark:bg-gray-950 dark:bg-gray-950/80 px-3 py-3 sm:px-4">
          <span className="flex flex-wrap items-center gap-1 text-sm font-medium text-gray-800">
            Languages (corpus / queries)
            <ReqBadge />
            <FieldHint text={TIP.languagesGroup} />
          </span>
          <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-500">
            <span>At least one required. Used with region for anchoring.</span>
            <FieldHint text={TIP.languagesHelp} />
          </p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2" role="group">
            {LANGUAGE_OPTIONS.map(({ code, label, tip }) => (
              <label
                key={code}
                className="inline-flex cursor-pointer items-center gap-1 text-sm text-gray-800"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                  checked={languages.has(code)}
                  onChange={(e) => toggleLanguage(code, e.target.checked)}
                  disabled={readOnly}
                  title={tip}
                />
                <span>
                  {label} <code className="text-xs text-gray-400">{code}</code>
                </span>
                <FieldHint text={tip} />
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="flex flex-wrap items-center gap-1 text-sm font-medium text-gray-800">
              Whitelist (one host per line)
              <OptBadge />
              <FieldHint text={TIP.whitelist} />
            </label>
            <textarea
              rows={3}
              className={fieldClass}
              value={whitelistText}
              onChange={(e) => setWhitelistText(e.target.value)}
              placeholder="has-sante.fr"
              readOnly={readOnly}
              title={TIP.whitelist}
            />
          </div>
          <div>
            <label className="flex flex-wrap items-center gap-1 text-sm font-medium text-gray-800">
              Blocklist (one host per line)
              <OptBadge />
              <FieldHint text={TIP.blocklist} />
            </label>
            <textarea
              rows={3}
              className={fieldClass}
              value={blocklistText}
              onChange={(e) => setBlocklistText(e.target.value)}
              placeholder="spam.example.com"
              readOnly={readOnly}
              title={TIP.blocklist}
            />
          </div>
        </div>

        <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600"
            checked={validateDns}
            onChange={(e) => setValidateDns(e.target.checked)}
            disabled={readOnly}
            title={TIP.validateDns}
          />
          <span className="flex flex-wrap items-center gap-1">
            Validate DNS (stronger; slower)
            <OptBadge />
            <FieldHint text={TIP.validateDns} />
          </span>
        </label>

        <div>
          <label className="flex flex-wrap items-center gap-1 text-sm font-medium text-gray-800" htmlFor="ds-feedback">
            Feedback for reshuffle
            <OptBadge />
            <FieldHint text={TIP.feedback} />
          </label>
          <textarea
            id="ds-feedback"
            rows={2}
            className={fieldClass}
            placeholder="e.g. More hospital regulators, fewer journals"
            value={userFeedback}
            onChange={(e) => setUserFeedback(e.target.value)}
            readOnly={readOnly}
            title={TIP.feedback}
          />
        </div>

        <div className="flex flex-wrap items-start gap-3 border-t border-gray-200 pt-4">
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              disabled={!onSuggest || !canSubmit || loading}
              onClick={() => runSuggest("initial")}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              title={TIP.btnInitial}
            >
              {loading ? "…" : "Get suggestions"}
            </button>
            <FieldHint text={TIP.btnInitial} />
          </span>
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              disabled={!onSuggest || !canSubmit || loading || sessionDomains.length === 0}
              onClick={() => runSuggest("more")}
              className="rounded-md border border-indigo-200 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
              title={TIP.btnMore}
            >
              Add 5 more
            </button>
            <FieldHint text={TIP.btnMore} />
          </span>
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              disabled={!onSuggest || !canSubmit || loading || lastBatch.length === 0}
              onClick={() => runSuggest("reshuffle")}
              className="rounded-md border border-gray-300 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:bg-gray-950 dark:bg-gray-950 disabled:cursor-not-allowed disabled:opacity-50"
              title={TIP.btnReshuffle}
            >
              Reshuffle
            </button>
            <FieldHint text={TIP.btnReshuffle} />
          </span>
        </div>

        {lastError ? (
          <div className="flex flex-wrap items-start gap-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <span className="min-w-0 flex-1">{lastError}</span>
            <FieldHint text={TIP.errorBox} />
          </div>
        ) : null}

        {sessionDomains.length > 0 ? (
          <p className="flex flex-wrap items-center gap-1 text-xs text-gray-500">
            <span>Session domains tracked:</span>
            <FieldHint text={TIP.sessionDomains} />
            <span className="font-mono text-gray-700 dark:text-gray-300">{sessionDomains.join(", ")}</span>
          </p>
        ) : null}

        {result.warnings?.length > 0 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <span className="inline-flex items-center gap-1 font-medium">
              Warnings
              <FieldHint text={TIP.warnings} />
            </span>
            <ul className="mt-1 list-inside list-disc">
              {result.warnings.map((w, i) => (
                <li key={i} title={w}>
                  {w}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {result.resolved_region ? (
          <p className="flex flex-wrap items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
            <span className="inline-flex items-center gap-1">
              Resolved region:
              <FieldHint text={`${TIP.resolvedRegion}${result.cache_hit ? ` ${TIP.cacheHit}` : ""}`} />
            </span>
            <strong>{result.resolved_region}</strong>
            {result.cache_hit ? <span title={TIP.cacheHit}> (cache hit)</span> : null}
          </p>
        ) : null}

        {result.suggestions?.length > 0 ? (
          <div>
            <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Suggestions
              <FieldHint text={TIP.suggestionsHeading} />
            </h3>
            <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
              {result.suggestions.map((s, i) => (
                <li
                  key={`${s.hostname}-${i}`}
                  className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-start sm:justify-between"
                  title={TIP.suggestionRow}
                >
                  <code className="text-sm font-semibold text-indigo-900" title={`${s.hostname}: ${s.rationale || ""}`}>
                    {s.hostname}
                  </code>
                  <span className="text-sm text-gray-600 dark:text-gray-400" title={s.rationale || TIP.suggestionRow}>
                    {s.rationale}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {result.overlap_with_previous?.length > 0 ? (
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
            <span className="inline-flex items-center gap-1 font-medium text-blue-900">
              Overlap with previous batch
              <FieldHint text={TIP.overlap} />
            </span>
            <ul className="mt-1 space-y-1">
              {result.overlap_with_previous.map((o, i) => (
                <li key={i} title={`${o.domain}: ${o.note || ""}`}>
                  <code className="font-mono">{o.domain}</code> — {o.note}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {result.rejected?.length > 0 ? (
          <details className="text-sm text-gray-600 dark:text-gray-400">
            <summary className="flex cursor-pointer list-none items-center gap-1 font-medium text-gray-800 marker:hidden [&::-webkit-details-marker]:hidden">
              <span aria-hidden className="select-none text-gray-400">
                ▸
              </span>
              Rejected candidates
              <FieldHint text={TIP.rejected} />
            </summary>
            <ul className="mt-2 max-h-40 overflow-auto font-mono text-xs">
              {result.rejected.map((r, i) => (
                <li key={i} title={`${r.candidate}: ${r.reason}`}>
                  {r.candidate}: {r.reason}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </div>
  )
}
